import {List} from 'immutable';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {useStoreChangePump} from 'client/TeacherDashboard/ClassDetail/useStoreChangePump';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import type {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';

export interface ClassLtiSync {
  isReady: boolean;
  /** The class's school has an LTI deployment — gates whether the LMS column appears at all. */
  schoolHasLtiDeployment: boolean;
  /** The class is connected to an LMS course — false → per-row "cannot sync" info tooltip. */
  classroomConnectedToLti: boolean;
  /** Raw Mandark platform key (canvas / schoology / d2l_brightspace) for the help link + modal copy. */
  ltiPlatformName: string;
  schoolId: string | null;
  /** True when the assignment already has a classroom-scoped LTI line item (i.e. already synced). */
  isSynced: (assignmentId: string) => boolean;
}

// Classroom + its LTI meta — mirrors the legacy ClassroomV2 store / getAssignmentQuery meta context
// that hasLtiDeployment()/getLtiPlatformName()/isClassroomHasLtiCourse() read.
const getClassroomLtiQuery = (classroomId: string) =>
  resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classroomId])
    .include('school_v5')
    .withMeta('classroom_v1')
    .customQuery({
      meta: {context: {has_lti_course: true, lti_deployment: true, lti_platform_name: true}}
    });

// The class's assignments with their classroom-scoped settings, so getLtiLineItemIdFromSettings can
// read the per-class lti_line_item_id (presence = already synced). Mirrors the legacy assignment
// query's classrooms_v1 include. Only fetched once we know the column is shown (school has LTI).
// pageSize(250) caps this synced-status lookup: a class with >250 assignments could leave one past
// the cap seeded as not-synced (shows "Sync" until re-synced). Accepted — 250 covers any real class.
const getClassAssignmentsLtiQuery = (classroomId: string) =>
  resource('assignments_v3')
    .mandarkEndpoint(['classrooms_v1', classroomId, 'assignments_v3'])
    .include('classrooms_v1')
    // Only this class's join row is read; an unfiltered include also pulls the assignments' other
    // classrooms, which a co-teacher may not be allowed to see — the whole request then 403s
    .filter({included: {classrooms_v1: {id: classroomId}}})
    .pageSize(250);

// Mandark caches a failed interest forever, so one blip would hide the LMS column all session.
// Re-dirty an errored interest once per query (capped — a second failure just keeps it hidden).
const useRetryErroredInterestOnce = (
  query: {isResourceValid: () => boolean; invalidateInterest: () => void} | null
) => {
  const retriedRef = useRef(false);
  useEffect(() => {
    retriedRef.current = false;
  }, [query]);
  const isInvalid = Boolean(query && !query.isResourceValid());
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- reacts to derived cache state (an errored interest), not a user event */
  useEffect(() => {
    if (isInvalid && query && !retriedRef.current) {
      retriedRef.current = true;
      query.invalidateInterest();
    }
  }, [isInvalid, query]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */
};

/**
 * Per-class LMS (LTI) grade-sync context for the v2 assignments list, sourced from Mandark (the GQL
 * `assignmentsAsEducator` layer exposes no LTI fields). Returns the column-visibility + can-sync
 * gates, the platform, the school id (for the sync mutation), and a per-assignment synced lookup.
 */
export const useClassLtiSync = (classroomId: string | undefined): ClassLtiSync => {
  useStoreChangePump();

  // Inert in the cross-class view (no class id) — callable unconditionally alongside the cross-class hook.
  const classroomQuery = useMemo(
    () => (classroomId ? getClassroomLtiQuery(classroomId) : null),
    [classroomId]
  );
  useRetryErroredInterestOnce(classroomQuery);
  const {isReady: classroomReady, resources: classroomResources} = useMandarkQueries(
    classroomQuery ? [classroomQuery] : []
  );
  // A retrying errored interest reports populated while its data is still an empty stub — treat it
  // as absent (column hidden) rather than crash on missing model methods.
  const rawClassroom = classroomResources[0] as ClassroomModelV1 | undefined;
  const classroom = typeof rawClassroom?.getMeta === 'function' ? rawClassroom : undefined;

  const schoolHasLtiDeployment = Boolean(
    classroomReady && classroom && classroom.getMeta().hasLtiDeployment()
  );

  // Skip the heavier per-assignment fetch entirely unless the column is even shown.
  const assignmentsQuery = useMemo(
    () => (schoolHasLtiDeployment && classroomId ? getClassAssignmentsLtiQuery(classroomId) : null),
    [schoolHasLtiDeployment, classroomId]
  );
  useRetryErroredInterestOnce(assignmentsQuery);
  // Synced status is read best-effort from cache; in-session syncs are reflected by useLtiSync's local
  // state. A cross-session stale read can briefly show "Sync" until refetch — acceptable for now.
  const {isReady: assignmentsReady, resources: assignmentsResources} = useMandarkQueries(
    assignmentsQuery ? [assignmentsQuery] : []
  );
  // Same stub guard — a stub counting as loaded would seed every cell "not synced" for the session.
  const rawAssignments = assignmentsResources[0];
  const assignments = List.isList(rawAssignments)
    ? (rawAssignments as List<AssignmentModelV3>)
    : undefined;

  const syncedIds = useMemo(() => {
    const set = new Set<string>();
    assignments?.forEach((assignment) => {
      if (assignment.getLtiLineItemIdFromSettings({classroomId})) {
        set.add(assignment.getId());
      }
    });
    return set;
  }, [assignments, classroomId]);

  // Stable per-syncedIds identity so consumers can key memos on it (a fresh closure per render
  // defeats their dep arrays).
  const isSynced = useCallback((assignmentId: string) => syncedIds.has(assignmentId), [syncedIds]);

  return {
    isReady: classroomReady && (!schoolHasLtiDeployment || (assignmentsReady && !!assignments)),
    schoolHasLtiDeployment,
    classroomConnectedToLti: Boolean(
      classroomReady &&
      classroom &&
      (classroom.getMeta().isClassroomHasLtiCourse?.() ?? classroom.hasLtiCourse())
    ),
    ltiPlatformName: classroomReady && classroom ? classroom.getMeta().getLtiPlatformName() : '',
    schoolId: classroomReady && classroom ? (classroom.getSchool()?.getId?.() ?? null) : null,
    isSynced
  };
};

// Teacher's classrooms with LTI-deployment meta — drives the cross-class explanatory affordance
// (shown only when at least one class has a deployment). masqueradeStore keys the endpoint so it
// resolves the impersonated teacher under masquerade, matching the rest of the dashboard.
const getTeacherClassroomsLtiQuery = () =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .withMeta('classroom_v1')
    .customQuery({meta: {context: {lti_deployment: true}}})
    .pageSize(250);

export interface AnyClassLtiDeployment {
  isReady: boolean;
  anyClassHasLtiDeployment: boolean;
}

/**
 * Whether any of the teacher's classes has an LTI deployment — gates the cross-class affordance.
 * `enabled` keeps it inert in the class-scoped view so it can be called unconditionally.
 */
export const useAnyClassLtiDeployment = (enabled: boolean): AnyClassLtiDeployment => {
  useStoreChangePump();

  const query = useMemo(() => (enabled ? getTeacherClassroomsLtiQuery() : null), [enabled]);
  const {isReady, resources} = useMandarkQueries(query ? [query] : []);
  const classrooms = resources[0] as List<ClassroomModelV1> | undefined;

  return {
    isReady,
    anyClassHasLtiDeployment: Boolean(
      isReady && classrooms?.some((classroom) => classroom.getMeta().hasLtiDeployment())
    )
  };
};
